/**
 * Image cover editor — paste a URL or drop a file, instant preview.
 * The parent (SecurityOpsPanel / Product Library row) is responsible for
 * actually calling the API; this component only collects the input.
 */
import { useState } from "react";
import { X, Upload, Image as ImageIcon, Loader2 } from "lucide-react";

export const ProductImageEditModal = ({ product, onClose, onSaveUrl, onUploadFile }) => {
  const [mode, setMode] = useState("url"); // 'url' | 'file'
  const [url, setUrl] = useState(product.image_url || "");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const previewSrc = mode === "file" && file ? URL.createObjectURL(file) : url;

  const handleSave = async () => {
    setBusy(true);
    try {
      if (mode === "file") {
        if (!file) return;
        await onUploadFile(file);
      } else {
        await onSaveUrl(url.trim());
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="image-edit-modal"
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-2xl border border-[#C5A059] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 bg-[#0A192F] text-white">
          <div className="font-accent text-xs tracking-wider">REPLACE COVER · {product.name}</div>
          <button data-testid="modal-close-btn" onClick={onClose} className="text-white/70 hover:text-white">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 grid sm:grid-cols-2 gap-5">
          {/* Live preview */}
          <div className="flex flex-col">
            <div className="font-accent text-[10px] text-slate-500 mb-2 tracking-wider">PREVIEW</div>
            <div className="aspect-[2/3] bg-[#0A192F] border border-slate-200 flex items-center justify-center">
              {previewSrc ? (
                <img src={previewSrc} alt="preview" className="w-full h-full object-contain" />
              ) : (
                <ImageIcon size={32} className="text-slate-400" />
              )}
            </div>
          </div>

          {/* Inputs */}
          <div className="flex flex-col">
            <div className="flex gap-1 mb-3">
              {[
                { id: "url", label: "PASTE URL" },
                { id: "file", label: "UPLOAD FILE" },
              ].map((t) => (
                <button
                  key={t.id}
                  data-testid={`modal-tab-${t.id}`}
                  onClick={() => setMode(t.id)}
                  className={`flex-1 text-[10px] font-accent tracking-wider py-2 px-3 ${
                    mode === t.id
                      ? "bg-[#0A192F] text-white"
                      : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {mode === "url" ? (
              <textarea
                data-testid="modal-url-input"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://… or /api/product-image/…"
                rows={5}
                className="text-xs font-mono p-3 border border-slate-300 focus:border-[#C5A059] focus:outline-none resize-none"
              />
            ) : (
              <div
                data-testid="modal-file-dropzone"
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  const f = e.dataTransfer.files?.[0];
                  if (f) setFile(f);
                }}
                className={`flex-1 border-2 border-dashed flex flex-col items-center justify-center gap-2 p-6 text-center cursor-pointer ${
                  dragOver ? "border-[#C5A059] bg-[#FBF8F1]" : "border-slate-300 bg-slate-50"
                }`}
                onClick={() => document.getElementById("modal-file-input").click()}
              >
                <Upload size={20} className="text-slate-400" />
                <div className="text-xs text-slate-600">
                  {file ? file.name : "Drop image here or click to browse"}
                </div>
                <div className="text-[10px] text-slate-400">PNG · JPEG · WebP · GIF · ≤ 4MB</div>
                <input
                  id="modal-file-input"
                  data-testid="modal-file-input"
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </div>
            )}

            <div className="flex gap-2 mt-4">
              <button
                onClick={onClose}
                className="px-4 py-2 text-xs font-accent text-slate-600 hover:text-[#0A192F] border border-slate-300"
              >
                CANCEL
              </button>
              <button
                data-testid="modal-save-btn"
                onClick={handleSave}
                disabled={busy || (mode === "file" && !file) || (mode === "url" && !url.trim())}
                className="flex-1 px-4 py-2 text-xs font-accent text-white bg-[#0A192F] hover:bg-[#0A192F]/90 disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                {busy ? (
                  <><Loader2 size={12} className="animate-spin" /> SAVING…</>
                ) : (
                  <>SAVE</>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
